import { EnumDialogName } from '@calm/evexia-analytics';
import { SxProps, Theme } from '@rhg/libs/material';
import SimpleDialog, {
    SimpleDialogProps,
} from '@rhg/libs/material/simple-dialog';
import {
    IconColor,
    DialogButton,
    BodyTextAlign,
} from '@rhg/libs/material/simple-dialog/types';
import {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useState,
    useEffect,
} from 'react';

export const DEFAULT_TIMEOUT = 2000;
export type ToastDialogButton = Omit<DialogButton, 'onClick'>;

export interface ToastDialogState {
    toast: (options: ToastDialogOptions) => void;
    isOpen: boolean;
}
export const ToastDialogContext = createContext<ToastDialogState>(null!);

export interface ToastDialogOptions {
    trackDialog?: EnumDialogName;
    body?: string;
    bodyTextAlign?: BodyTextAlign;
    content?: ReactElement | ReactElement[] | null;
    title?: string;
    icon?: ReactElement;
    fullScreen?: boolean;
    background?: SimpleDialogProps['background'];
    iconColor?: IconColor;
    sx?: SxProps<Theme>;
    SimpleDialogProps?: Partial<
        Omit<SimpleDialogProps, 'iconContainerOverride'>
    >;
    timeout?: number;
}

interface Props {
    children: ReactNode;
}
export const ToastDialogProvider = ({ children }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<ToastDialogOptions | undefined>(
        undefined
    );

    const onClose = useCallback(() => {
        // Only flip isOpen; keep options until animation finishes to avoid blank flash
        setIsOpen(false);
    }, []);

    const toast = useCallback(
        (options?: ToastDialogOptions) => {
            setOptions(options);
            setIsOpen(true);
            setTimeout(onClose, options?.timeout ?? DEFAULT_TIMEOUT);
        },
        [onClose]
    );

    // After closing, clear options after a short delay (matches close animation duration)
    // to avoid flash of blank dialog before SimpleDialog close animation finishes
    useEffect(() => {
        if (!isOpen && options) {
            const timeout = setTimeout(
                () => setOptions(undefined),
                options?.timeout ?? DEFAULT_TIMEOUT
            );
            return () => clearTimeout(timeout);
        }
    }, [isOpen, options]);

    return (
        <>
            <ToastDialogContext.Provider value={{ toast, isOpen }}>
                {children}
            </ToastDialogContext.Provider>
            <SimpleDialog
                icon={options?.icon}
                iconColor={options?.iconColor}
                trackDialog={options?.trackDialog || EnumDialogName.Toast}
                buttons={[]}
                hideCloseButton={true}
                disableMobileFullscreen={true}
                dialogMaxWidth="md"
                fullWidth
                isOpen={isOpen}
                isLiveRegion
                title={options?.title}
                onClose={onClose}
                bodyText={options?.body}
                bodyTextAlign={options?.bodyTextAlign}
                content={options?.content}
                {...options?.SimpleDialogProps}
            />
        </>
    );
};
